import { useMemo } from "react";

import { OwnerType } from "@/domain/_shared/lib/owner-types";
import { createOperationClient } from "@/services/react-query.service/graphql-client";
import { queryKeys } from "@/services/react-query.service/query-keys";
import { useQuery } from "@tanstack/react-query";

import { fileListForOwnerQueryDocument } from "./file.gql";

export interface UseGetFileListForOwnerProps {
  ownerId: string;
  ownerType: OwnerType;
}

const SCAN_POLL_INTERVAL_MS = 3000;

export const useGetFileListByOwner = ({ ownerId, ownerType }: UseGetFileListForOwnerProps) => {
  const queryResult = useQuery({
    queryKey: queryKeys.files.list(ownerId, ownerType),
    enabled: Boolean(ownerId),
    queryFn: async () => {
      const client = createOperationClient("fileListForOwner");
      return await client.request(fileListForOwnerQueryDocument, { input: { ownerId, ownerType } });
    },
    refetchInterval: (query) => {
      const hasInProgressScan = query.state.data?.fileListForOwner?.some(
        (file) => file.scanStatus === "PENDING" || file.scanStatus === "SCANNING",
      );
      return hasInProgressScan ? SCAN_POLL_INTERVAL_MS : false;
    },
  });

  const fileList = useMemo(() => {
    if (!queryResult.data?.fileListForOwner) {
      return [];
    }

    return queryResult.data.fileListForOwner.filter(
      (file) => file.scanStatus !== "PENDING" && file.scanStatus !== "SCANNING",
    );
  }, [queryResult.data]);

  return {
    queryResult,
    fileList,
  };
};
